import React, { useContext, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import PageHeaderContext from './../pageHeaderContext.js';

// Sticky top bar: back chevron (mobile-only) | title | optional rightSlot.
// Lives inside the page content so it participates in that page's layout
// and transitions, instead of hanging as a global overlay. Marks itself
// as active in PageHeaderContext so the global back button in MainPage
// hides while this component is mounted (no duplicate). The flag flip
// happens in useLayoutEffect — synchronous before paint — otherwise the
// global button briefly renders on the first frame and the user sees it
// swap for the PageHeader's own back button.
const PageHeader = ({ title, rightSlot }) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 48em)');
  const { setActive } = useContext(PageHeaderContext);

  useLayoutEffect(() => {
    if (isDesktop) return undefined;
    setActive(true);
    return () => setActive(false);
  }, [setActive, isDesktop]);

  // Desktop already has AppShell.Header as top chrome; stacking a
  // PageHeader (plus its 44px spacer) on top of that just creates a
  // dead 44px gap between AppShell.Header and the actual page content.
  // Skip PageHeader on desktop — mobile is the only place that needs it.
  if (isDesktop) return null;

  // 44 px bar pinned to the viewport. Fixed (not sticky) because sticky
  // was riding the AppShell.Main reflow — mobile URL-bar collapse and
  // any flex-pressure on the content column were visually shrinking it.
  // A spacer sibling of the same height reserves the layout space so
  // content below starts at the right spot.
  const headerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 50,
    background: 'var(--mantine-color-body)',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    height: 48,
    minHeight: 48,
    flexShrink: 0,
    padding: '0 4px',
  };
  return (
    <>
    <div style={headerStyle}>
      {/* Hide the back chevron when there's no in-tab history
          (Instagram pattern — direct-landed pages don't show back).
          navCount is the same session-tracked counter the global back
          button uses in MainPage. */}
      {!isDesktop && (() => {
        let navCount = 0;
        try { navCount = parseInt(sessionStorage.getItem('_navCount') || '0', 10); } catch {}
        return navCount > 0;
      })() ? (
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          title="Back"
          style={{
            width: 48,
            height: 48,
            flex: '0 0 48px',
            padding: 0,
            paddingLeft: 2,
            border: 'none',
            background: 'transparent',
            color: 'var(--mantine-color-text)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {/* Instagram-style chevron, inline SVG so it renders crisp on
              any theme/background without filter hacks. */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      ) : (
        // No back chevron: for text titles keep a 48px left spacer so
        // the centered title still looks symmetric to the right spacer;
        // for custom-node titles (e.g. a search input) drop the spacer
        // so the input can stretch to the viewport edge.
        <div style={typeof title === 'string' ? { width: 48, flex: '0 0 48px' } : { width: 0, flex: '0 0 0' }} />
      )}
      <div
        style={typeof title === 'string'
          ? {
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 18,
              fontWeight: 600,
              textAlign: 'center',
            }
          : {
              // Non-string title (e.g. a TextInput used as a search bar
              // on /addEvent). Just reserve the flex-1 slot; let the
              // child own its own styling.
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
            }
        }
        title={typeof title === 'string' ? title : undefined}
      >
        {title}
      </div>
      {/* Right-side spacer matches the back button so the title stays
          truly centered when no rightSlot is supplied. */}
      {rightSlot
        ? <div style={{ flex: '0 0 auto', paddingRight: 4 }}>{rightSlot}</div>
        // Non-string title (custom node like a search input): let it
        // stretch to the right edge, no reserved right spacer.
        : typeof title === 'string'
          ? <div style={{ flex: '0 0 48px', width: 48 }} />
          : <div style={{ flex: '0 0 0', width: 0 }} />}
    </div>
    {/* Layout spacer so content below starts at the real top of the
        document instead of underneath the fixed bar. */}
    <div style={{ height: 48, flexShrink: 0 }} aria-hidden="true" />
    </>
  );
};

export default PageHeader;
